import { cx } from '@linaria/core';
import { useRef, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { breakpoints } from '@ui/css';
import { Tooltip } from '@ui/kit';
import { CrossIcon, PartialCheckmarkIcon, RoundedCheckmarkIcon } from '@ui/kit/icons';
import { canValidateOpgavekompleksetTask } from '@utils/component-framework';
import { useMatchBreakpoint, CSSRefTransition } from '@utils/react';
import { useOpgavekompleksetComponent } from '../../../state/context.data.js';
import { useCurrentTask } from '../../../state/hook.current-task.js';
import { useOpgavekompleksetEntry } from '../../../state/hook.entry.js';
import { useGoToTaskMutation } from '../../../state/mutations/task.go-to.js';
import { getProgressionType } from '../../../state/util.get-progression-type.js';
import { shouldShowFeedback } from '../../../state/util.should-show-feedback.js';
import { getTaskEntryState } from '../../../utils/get-task-entry-state.js';
import { getTaskTitle } from '../../../utils/get-task-title.js';
import { wrapper, iconWrapper, iconCrossfadeDurationMs, bullet, bulletIcon } from './atom.bullet.style.js';
import { useBulletScale } from './hook.bullet-scale.js';
import { jsx } from 'react/jsx-runtime';

function Bullet(props) {
  const ref = useRef(null);
  const goToTask = useGoToTaskMutation();
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const component = useOpgavekompleksetComponent();
  const shouldDisplayResult = shouldShowFeedback(component.data.settings.flowType);
  const isCurrentTask = useCurrentTask().task?.id === props.task.id;
  const stats = useOpgavekompleksetEntry({
    select: state => state?.data.tasks[props.task.id]?.stats
  });
  const isSkipped = useOpgavekompleksetEntry({
    select: state => state?.data.tasks[props.task.id]?.state.skipped ?? true
  });
  const hasValidated = useOpgavekompleksetEntry({
    select: state => state?.data.tasks[props.task.id]?.state.validated
  });
  const hasInteracted = useOpgavekompleksetEntry({
    select: state => canValidateOpgavekompleksetTask(props.task) ? Object.keys(state?.data.tasks[props.task.id]?.details ?? {}).length > 0 : state?.data.tasks[props.task.id]?.state.closed
  });
  const canValidate = canValidateOpgavekompleksetTask(props.task);
  const state = (() => {
    if (isCurrentTask) {
      return "active";
    }
    if (isSkipped || !hasInteracted) {
      return;
    }
    if (!shouldDisplayResult || !hasValidated || !canValidate) {
      return "indeterminate";
    }
    return getTaskEntryState(stats);
  })();
  const icon = (() => {
    switch (state) {
      case "active":
      case "indeterminate":
        return /*#__PURE__*/jsx("span", {
          className: bulletIcon
        });
      case "correct":
        return /*#__PURE__*/jsx(RoundedCheckmarkIcon, {});
      case "partial":
        return /*#__PURE__*/jsx(PartialCheckmarkIcon, {
          style: {
            marginTop: "0.5px",
            marginLeft: "0.5px"
          }
        });
      case "incorrect":
        return /*#__PURE__*/jsx(CrossIcon, {
          strokeWidth: 1.5,
          height: 10
        });
    }
  })();
  useBulletScale(ref, props.listRef, [props.task.id]);
  const Comp = getProgressionType(component.data.settings.flowType) === "free" && !isMobile ? "button" : "div";
  const onClick = () => {
    if (Comp !== "button") {
      return;
    }
    goToTask(props.task.id);
  };
  const tooltipBody = (() => {
    switch (state) {
      case "active":
        return "Denne opgave";
      case "correct":
        return `Løst rigtigt - ${getTaskTitle(props.task)}`;
      case "incorrect":
        return `Løst forkert - ${getTaskTitle(props.task)}`;
      case "partial":
        return `Løst delvist rigtigt - ${getTaskTitle(props.task)}`;
      case "indeterminate":
      default:
        return getTaskTitle(props.task);
    }
  })();

  // Ensure that once the bullet activates, it is automatically scrolled to be
  // in view.
  useEffect(() => {
    setTimeout(() => {
      if (isCurrentTask) {
        const scroller = props.listRef.current;
        const bullet = ref.current;
        if (!scroller || !bullet) {
          return;
        }
        const center = scroller.clientWidth / 2;
        scroller.scrollTo({
          left: bullet.offsetLeft - center,
          behavior: "smooth"
        });
      }
    }, 50);
  }, [isCurrentTask, props.listRef]);
  return /*#__PURE__*/jsx("li", {
    ref: ref,
    children: /*#__PURE__*/jsx(Tooltip, {
      body: tooltipBody,
      placement: "top",
      ariaType: "description",
      children: tooltipRef => /*#__PURE__*/jsx(Comp, {
        onClick: onClick,
        className: wrapper,
        ref: tooltipRef,
        children: /*#__PURE__*/jsx("div", {
          className: cx(bullet, isCurrentTask && "isCurrentTask"),
          "data-state": state ?? "default",
          children: /*#__PURE__*/jsx(TransitionGroup, {
            component: null,
            children: /*#__PURE__*/jsx(CSSRefTransition, {
              timeout: iconCrossfadeDurationMs,
              classNames: iconWrapper,
              children: ref => /*#__PURE__*/jsx("span", {
                ref: ref,
                className: iconWrapper,
                children: icon
              })
            }, state)
          })
        })
      })
    })
  });
}

export { Bullet };
//# sourceMappingURL=atom.bullet.js.map
